import type { ReactNode } from 'react';
import { useCrmAccess, useMigrationOn } from '@/hooks/migrationQueries';
import { IconAlert } from './Icons';

const dayMonth = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

interface MigrationBarProps {
  message: ReactNode;
  action?: { href: string; label: string } | null;
  /** Na tela de entrar: no fluxo da página, sem as margens do conteúdo nem a trava no topo. */
  standalone?: boolean;
}

/** A faixa vermelha do aviso de migração: título, a mensagem de cada caso e o botão. */
function MigrationBar({ message, action = null, standalone = false }: MigrationBarProps) {
  return (
    <div className={`migration-banner${standalone ? ' migration-banner--standalone' : ''}`} role="status">
      <div className="migration-banner-text">
        <span className="migration-banner-icon" aria-hidden>
          <IconAlert size={22} />
        </span>
        <div>
          <p className="migration-banner-title">Atenção: o Painel comercial está mudando para o CRM</p>
          <p className="migration-banner-message">{message}</p>
        </div>
      </div>
      {action ? (
        <a className="migration-banner-action" href={action.href} target="_blank" rel="noopener noreferrer">
          {action.label} <span aria-hidden>→</span>
        </a>
      ) : null}
    </div>
  );
}

/**
 * O aviso de migração para o Last CRM: faixa vermelha presa no topo de todas as páginas, para
 * todos. O membro recebe o link pessoal para criar a senha (ou o login, se já entra); o
 * administrador, o login do CRM; quem ainda não tem link, o aviso sem botão. Só some com o aviso
 * desligado (sem as variáveis do CRM no servidor).
 */
export function MigrationBanner() {
  const { data } = useCrmAccess();
  if (!data || data.status === 'desligado') return null;

  switch (data.status) {
    case 'definir-senha':
      return (
        <MigrationBar
          message={<>Crie agora a sua senha no CRM. O link é só seu e vale até {dayMonth.format(new Date(data.expiresAt))}.</>}
          action={{ href: data.url, label: 'Criar minha senha no CRM' }}
        />
      );
    case 'com-acesso':
      return <MigrationBar message="Seu acesso ao CRM já está pronto." action={{ href: data.url, label: 'Abrir o CRM' }} />;
    case 'admin':
      return (
        <MigrationBar
          message="Os membros recebem aqui o link pessoal para criar a senha no CRM."
          action={{ href: data.url, label: 'Abrir o CRM' }}
        />
      );
    default:
      return <MigrationBar message="Seu acesso ao CRM ainda está sendo preparado. Se precisar, fale com um administrador." />;
  }
}

/** Na tela de entrar: a mesma faixa, sem link (ele é pessoal e só aparece depois de entrar). */
export function LoginMigrationBanner() {
  const { data } = useMigrationOn();
  if (!data?.ligado) return null;
  return <MigrationBar standalone message="Entre com o seu usuário para ver o seu link de acesso ao CRM." />;
}
